import { DoorClosed, DoorOpen, ImagePlus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ImageUpload } from "@/components/shared/image-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFile, useUploadImage } from "@/hooks/use-files";
import type { PersonalOfficeDoorStatus } from "@/lib/enums";
import {
  DEFAULT_OFFICE_THEME,
  normalizeOfficeTheme,
  OFFICE_RUG_SWATCHES,
  OFFICE_WALL_SWATCHES,
  type OfficeTheme,
} from "@/lib/office/office-theme";
import { MAX_OFFICE_DECOR } from "@/lib/office/personal-office-layout";
import { cn } from "@/lib/utils";
import { UNLOCKS } from "./constants";
import { OfficePreview } from "./OfficePreview";

export type OfficeStudioValues = {
  displayName: string;
  doorStatus: PersonalOfficeDoorStatus;
  decorIds: string[];
  wallArtFileId: string | null;
  wallArtPlaceholder: string | null;
  theme: OfficeTheme;
};

type OfficeChapter = "door" | "furniture" | "photo" | "paint";

const CHAPTERS: Array<{ id: OfficeChapter; label: string }> = [
  { id: "door", label: "Door" },
  { id: "furniture", label: "Furniture" },
  { id: "photo", label: "Photo" },
  { id: "paint", label: "Paint" },
];

type OfficeStudioProps = {
  values: OfficeStudioValues;
  onChange: (patch: Partial<OfficeStudioValues>) => void;
  unlockedIds: string[];
  points: number;
  disabled?: boolean;
  showDoor?: boolean;
  /** Show live isometric preview above tabs. Default true; false in dressing-room tray. */
  showPreview?: boolean;
  layout?: "card" | "tray";
};

export function OfficeStudio({
  values,
  onChange,
  unlockedIds,
  points,
  disabled = false,
  showDoor = true,
  showPreview = true,
  layout = "card",
}: OfficeStudioProps) {
  const [chapter, setChapter] = useState<OfficeChapter>("door");
  const uploadImage = useUploadImage();
  const { data: wallFile } = useFile(values.wallArtFileId);
  const theme = normalizeOfficeTheme(values.theme ?? DEFAULT_OFFICE_THEME);
  const isTray = layout === "tray";

  function toggleDecor(id: string) {
    if (disabled) return;
    const prev = values.decorIds;
    if (prev.includes(id)) {
      onChange({ decorIds: prev.filter((x) => x !== id) });
      return;
    }
    if (prev.length >= MAX_OFFICE_DECOR) {
      // Replace oldest when at cap so the new pick always lands
      onChange({ decorIds: [...prev.slice(1), id] });
      return;
    }
    onChange({ decorIds: [...prev, id] });
  }

  async function handleWallPhoto(file: File) {
    if (disabled) return;
    try {
      const record = await uploadImage.mutateAsync(file);
      onChange({
        wallArtFileId: record.id,
        wallArtPlaceholder: record.placeholder,
      });
    } catch {
      // useUploadImage already toasts; avoid unhandled rejection
    }
  }

  return (
    <div className="space-y-4">
      {showPreview ? (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-[#0E1F40] shadow-inner">
          <OfficePreview
            displayName={values.displayName}
            doorStatus={values.doorStatus}
            decorIds={values.decorIds}
            wallArtFileId={values.wallArtFileId}
            wallArtPlaceholder={values.wallArtPlaceholder ?? wallFile?.placeholder ?? null}
            theme={theme}
            className="mx-auto block w-full max-w-[320px]"
            width={300}
            height={210}
          />
        </div>
      ) : null}

      <div
        className="flex flex-wrap gap-1 rounded-lg border border-border/60 bg-muted/40 p-1"
        role="tablist"
        aria-label="Office chapters"
      >
        {CHAPTERS.map((t) => {
          const active = chapter === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={disabled}
              onClick={() => setChapter(t.id)}
              className={cn(
                "cursor-pointer rounded-md px-2.5 py-1.5 text-xs font-bold tracking-wide transition-colors disabled:opacity-50",
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        className={cn(
          "min-h-[120px] space-y-3 rounded-lg border border-border/50 bg-background/80 px-3 py-3",
          isTray && "bg-card/80",
        )}
      >
        {chapter === "door" ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="office-plaque">Name on the door</Label>
              <Input
                id="office-plaque"
                value={values.displayName}
                maxLength={48}
                disabled={disabled}
                onChange={(e) => onChange({ displayName: e.target.value })}
                placeholder="Office name"
              />
            </div>
            {showDoor ? (
              <div className="space-y-2">
                <Label>Door</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={values.doorStatus === "open" ? "default" : "outline"}
                    disabled={disabled}
                    onClick={() => onChange({ doorStatus: "open" })}
                  >
                    <DoorOpen className="h-3.5 w-3.5" />
                    Open to guests
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={values.doorStatus === "closed" ? "default" : "outline"}
                    disabled={disabled}
                    onClick={() => onChange({ doorStatus: "closed" })}
                  >
                    <DoorClosed className="h-3.5 w-3.5" />
                    Closed
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Closed offices are private — only you can enter.
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {chapter === "furniture" ? (
          <div className="space-y-2">
            <Label>Desk decor</Label>
            <p className="text-xs text-muted-foreground">
              Place up to {MAX_OFFICE_DECOR} unlocked perks ({values.decorIds.length}/
              {MAX_OFFICE_DECOR} placed). Earn more by hanging out in HQ.
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {UNLOCKS.map((u) => {
                const unlocked = unlockedIds.includes(u.id) || points >= u.pts;
                const active = values.decorIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={!unlocked || disabled}
                    onClick={() => toggleDecor(u.id)}
                    className={cn(
                      "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm disabled:opacity-45",
                      active
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:bg-muted/40",
                    )}
                  >
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ background: u.swatch }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-foreground">{u.name}</span>
                      <span className="block text-[11px] text-muted-foreground">{u.desc}</span>
                    </span>
                    <span className="text-[10px] font-bold tracking-wide text-muted-foreground">
                      {unlocked ? (active ? "PLACED" : "UNLOCKED") : `${u.pts} PTS`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {chapter === "photo" ? (
          <div className="space-y-3">
            <div>
              <Label className="inline-flex items-center gap-1.5">
                <ImagePlus className="h-3.5 w-3.5" />
                Hang a photo
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Upload a picture for a framed wall print. Autosave hangs it on your pod.
              </p>
            </div>
            {(values.wallArtFileId || wallFile) && (
              <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-2">
                {wallFile?.thumbnailUrl || wallFile?.url || values.wallArtPlaceholder ? (
                  <img
                    src={
                      wallFile?.thumbnailUrl ??
                      wallFile?.url ??
                      values.wallArtPlaceholder ??
                      undefined
                    }
                    alt="Wall art"
                    className="h-16 w-16 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded bg-muted text-[10px] text-muted-foreground">
                    Loading…
                  </div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled || uploadImage.isPending}
                  onClick={() => onChange({ wallArtFileId: null, wallArtPlaceholder: null })}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </Button>
              </div>
            )}
            <ImageUpload
              onSelect={(f) => void handleWallPhoto(f)}
              isUploading={uploadImage.isPending}
              disabled={disabled}
            />
          </div>
        ) : null}

        {chapter === "paint" ? (
          <div className="space-y-4">
            <SwatchBlock
              label="Wall color"
              options={OFFICE_WALL_SWATCHES}
              value={theme.wallColor}
              disabled={disabled}
              onPick={(c) => onChange({ theme: { ...theme, wallColor: c } })}
            />
            <SwatchBlock
              label="Rug / floor accent"
              options={OFFICE_RUG_SWATCHES}
              value={theme.rugColor}
              disabled={disabled}
              onPick={(c) => onChange({ theme: { ...theme, rugColor: c } })}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SwatchBlock({
  label,
  options,
  value,
  onPick,
  disabled,
}: {
  label: string;
  options: readonly string[];
  value: string;
  onPick: (c: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-bold tracking-[0.1em] text-muted-foreground uppercase">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((c) => (
          <button
            key={c}
            type="button"
            disabled={disabled}
            aria-label={`${label} ${c}`}
            onClick={() => onPick(c)}
            className={cn(
              "h-7 w-7 cursor-pointer rounded-md border-2 p-0 disabled:opacity-50",
              value === c ? "border-primary ring-2 ring-primary/30" : "border-border",
            )}
            style={{ background: c }}
          />
        ))}
      </div>
    </div>
  );
}
